import { DeterministicView } from '@bingads-webui/deterministic-view';
import { BackboneSchema as backboneSchema } from '@bingads-webui/backbone-schema';
import { InputControl } from '@bingads-webui/input-control';
import _ from 'underscore';
import $ from 'jquery';
import template from './bid-strategy-view-template.pug';

export const getBidStrategyView = ({
  i18n, pageContext, config, fixedBidSchema, percentageBidSchema, payPerStayBidSchema,
}) => {
  if (!i18n || !pageContext || !config || !fixedBidSchema ||
    !percentageBidSchema || !payPerStayBidSchema) {
    return {};
  }
  /**
   * Shared view for Hotel Ads bid strategy setup section
   * @extends Backbone.View
   */
  class BidStrategyView extends DeterministicView {
    initialize(options) {
      this.strategy = {
        fixed: 'fixed',
        percentage: 'percentage',
        payperstay: 'payperstay',
        empty: 'empty',
      };

      this.currentTemplate = options.customTemplate || template;
      this.i18n = i18n;

      this.isAmountRequired = !_.result(options, 'allowEmptyStrategy', true);
      this.isPayPerStayEnabled = _.result(pageContext.Permissions.dynamic, 'PayPerStay', false);

      // todo: remove below readonly flags when DE limitation is resolved
      this.isReadonly = _.result(options, 'isReadonly', false);
      this.disabledAttr = this.isReadonly ? { disabled: 'disabled' } : {};
      this.isPPSReadOnly = this.isReadonly || !_.result(options, 'enablePPS', false);
      this.disabledPPSAttr = this.isPPSReadOnly ? { disabled: 'disabled' } : {};

      this.initFixedBidInput(this.isAmountRequired);
      this.initPercentageBidInput(this.isAmountRequired);
      this.initPayPerStayBidInput(this.isAmountRequired);

      this.setState({
        bidStrategy: options.strategy || this.strategy.fixed,
        bidAmount: options.bidAmount,
      });

      this.on('rendered', () => {
        const bidStrategy = this.model.get('bidStrategy');
        if (bidStrategy === this.strategy.fixed) {
          $('label.radio.fixed').focus();
        } else if (bidStrategy === this.strategy.percentage) {
          $('label.radio.percentage').focus();
        } else if (bidStrategy === this.strategy.payperstay) {
          $('label.radio.payperstay').focus();
        } else if (bidStrategy === this.strategy.empty) {
          $('label.radio.empty').focus();
        }
      }, this);
    }

    get events() {
      return {
        'change .bid-strategy-radio': ({ target }) => {
          this.setState({ bidStrategy: target.value });
        },
      };
    }

    get template() {
      return this.currentTemplate;
    }

    get bidInput() {
      const { bidStrategy } = super.getState();

      return _.result({
        fixed: () => this.getChild('fixed-bid-input'),
        percentage: () => this.getChild('percentage-bid-input'),
        payperstay: () => this.getChild('payperstay-bid-input'),
      }, bidStrategy);
    }

    setState(props) {
      const { bidStrategy, bidAmount, showError } = props;

      // todo: remove below readonly flags when DE limitation is resolved
      this.isReadonly = _.result(props, 'isReadonly', this.isReadonly);
      this.disabledAttr = this.isReadonly ? { disabled: 'disabled' } : {};

      if (!_.isUndefined(bidStrategy)) {
        this.model.set({ bidStrategy });
      }

      if (!_.isUndefined(bidAmount)) {
        const value = i18n.formatDecimalToFixed(bidAmount);

        if (bidStrategy === this.strategy.fixed) {
          this.fixedBidModel.set('Amount', value);
          this.getChild('fixed-bid-input').setState({ value, readonly: this.isReadonly });
        } else if (bidStrategy === this.strategy.percentage) {
          this.percentageBidModel.set('Amount', value);
          this.getChild('percentage-bid-input').setState({ value, readonly: this.isReadonly });
        } else {
          this.payPerStayBidModel.set('Amount', value);
          this.getChild('payperstay-bid-input').setState({ value });
        }
        this.trigger('input-changed');
      }

      if (!_.isUndefined(showError)) {
        if (this.bidInput) {
          this.bidInput.setState({ showError });
        }
      }

      return this;
    }

    getState() {
      switch (this.model.get('bidStrategy')) {
        case this.strategy.fixed:
          return {
            isValid: this.getChild('fixed-bid-input').getState().isValid,
            bid: this.fixedBidModel.toJSON(),
          };
        case this.strategy.percentage:
          return {
            isValid: this.getChild('percentage-bid-input').getState().isValid,
            bid: this.percentageBidModel.toJSON(),
          };
        case this.strategy.payperstay:
          return {
            isValid: this.getChild('payperstay-bid-input').getState().isValid,
            bid: this.payPerStayBidModel.toJSON(),
          };
        default:
          return {
            isValid: !this.isAmountRequired,
            bid: { Amount: null },
          };
      }
    }

    initFixedBidInput() {
      const FixedBidModel = backboneSchema(fixedBidSchema, i18n, ['Amount', '@odata.type']);

      this.fixedBidModel = new FixedBidModel();
      const fixedBidInput = new InputControl({
        entity: this.fixedBidModel,
        field: 'Amount',
        addonText: config.get('AccountCurrencyCode'),
        readonly: this.isReadonly,
      });

      this.setChild('fixed-bid-input', fixedBidInput);

      this.listenTo(fixedBidInput, 'input-changed', () => {
        this.trigger('input-changed');
      });
    }

    initPercentageBidInput() {
      const PercentageBidModel = backboneSchema(percentageBidSchema, i18n, ['Amount', '@odata.type']);

      this.percentageBidModel = new PercentageBidModel();
      const percentageBidInput = new InputControl({
        entity: this.percentageBidModel,
        field: 'Amount',
        addonText: pageContext.DecimalFormatter.percentSymbol,
        readonly: this.isReadonly,
      });

      this.setChild('percentage-bid-input', percentageBidInput);

      this.listenTo(percentageBidInput, 'input-changed', () => {
        this.trigger('input-changed');
      });
    }

    initPayPerStayBidInput() {
      const PayPerStayBidModel = backboneSchema(payPerStayBidSchema, i18n, ['Amount', '@odata.type']);

      this.payPerStayBidModel = new PayPerStayBidModel();
      const payPerStayBidInput = new InputControl({
        entity: this.payPerStayBidModel,
        field: 'Amount',
        addonText: pageContext.DecimalFormatter.percentSymbol,
      });

      this.setChild('payperstay-bid-input', payPerStayBidInput);

      this.listenTo(payPerStayBidInput, 'input-changed', () => {
        this.trigger('input-changed');
      });
    }
  }
  return {
    BidStrategyView,
  };
};
